import { ArrowLeft, ChevronDown, ChevronUp, Plus, Target, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { TrainingModeCode } from '@golf/common';
import { ApiError } from '../../lib/api.ts';
import { MODES } from '../../lib/modes.ts';
import type { CustomExercise, Workout } from '../../lib/planning.ts';
import {
    archiveWorkout, boundLabel, createWorkout, listExercises, listWorkouts,
    setWorkoutItems, updateWorkout,
} from '../../lib/planning.ts';
import { ModeIcon } from './ModeIcon.tsx';

/* Composer une séance : la suite d'exercices qu'on suivra au practice.
 *
 * Une étape désigne SOIT un mode intégré, SOIT un exercice personnalisé —
 * jamais les deux, jamais aucun. La base tient cette exclusivité par contrainte
 * ; ici elle se voit dans le type de `Step`.
 */

interface Step {
    /** Clé de rendu stable : les étapes se déplacent, et un index comme clé
     *  ferait ré-utiliser le mauvais nœud du DOM après un déplacement. */
    key: string;
    mode: TrainingModeCode | null;
    exerciseId: string | null;
    isPutting: boolean;
    label: string;
    detail: string;
}

let seq = 0;
const nextKey = () => `s${++seq}`;

export function WorkoutComposer() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const creating = id === undefined || id === 'nouvelle';

    const [exercises, setExercises] = useState<CustomExercise[]>([]);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [steps, setSteps] = useState<Step[]>([]);
    const [picker, setPicker] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        void listExercises().then(setExercises).catch(() => setError('Chargement impossible'));
        if (creating) return;
        void listWorkouts().then((list) => {
            const w: Workout | undefined = list.find((x) => x.id === id);
            if (!w) { setError('Séance introuvable'); return; }
            setName(w.name);
            setDescription(w.description ?? '');
            setSteps(w.items.map((it) => ({
                key: nextKey(),
                mode: it.mode,
                exerciseId: it.exerciseId,
                isPutting: it.isPutting,
                label: it.isPutting ? 'Putting'
                    : it.mode ? MODES.find((m) => m.mode === it.mode)?.label ?? it.mode
                        : it.exerciseName ?? 'Exercice supprimé',
                detail: it.isPutting ? 'Sur le green' : describe(it.ballsPerRound, it.rounds),
            })));
        }).catch(() => setError('Chargement impossible'));
    }, [id, creating]);

    const move = (i: number, by: -1 | 1): void => {
        setSteps((prev) => {
            const j = i + by;
            if (j < 0 || j >= prev.length) return prev;
            const next = [...prev];
            [next[i], next[j]] = [next[j]!, next[i]!];
            return next;
        });
    };

    const addMode = (m: TrainingModeCode): void => {
        const meta = MODES.find((x) => x.mode === m)!;
        setSteps((p) => [...p, {
            key: nextKey(), mode: m, exerciseId: null, isPutting: false,
            label: meta.label, detail: 'Mode intégré',
        }]);
        setPicker(false);
    };

    /** Le putting comme étape.
     *
     *  Ni mode ni exercice : il ne vit pas dans la même table — pas de sac,
     *  pas de séries, pas d'issue de balle — et le déroulé ira chercher son
     *  exécution du côté des parties. « Trois exercices au practice puis dix
     *  trous de putting » reste pourtant un plan tout à fait ordinaire. */
    const addPutting = (): void => {
        setSteps((p) => [...p, {
            key: nextKey(), mode: null, exerciseId: null, isPutting: true,
            label: 'Putting', detail: 'Sur le green, seul ou à plusieurs',
        }]);
        setPicker(false);
    };

    const addExercise = (e: CustomExercise): void => {
        setSteps((p) => [...p, {
            key: nextKey(), mode: null, exerciseId: e.id, isPutting: false,
            label: e.name, detail: describe(e.ballsPerRound, e.rounds),
        }]);
        setPicker(false);
    };

    const save = async (): Promise<void> => {
        if (!name.trim()) { setError('Donnez un nom à cette séance'); return; }
        setBusy(true); setError(null);
        try {
            const wid = creating
                ? (await createWorkout({ name: name.trim(), description: description.trim() || null })).id
                : id;
            if (!creating) {
                await updateWorkout(wid, { name: name.trim(), description: description.trim() || null });
            }
            // La composition part en un seul appel, dans l'ordre affiché :
            // ajouter, déplacer et retirer sont le même geste vu du serveur.
            await setWorkoutItems(wid, steps.map((s) => ({
                mode: s.mode, exerciseId: s.exerciseId, isPutting: s.isPutting,
            })));
            void navigate('/entrainement');
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Enregistrement impossible');
            setBusy(false);
        }
    };

    const remove = async (): Promise<void> => {
        if (creating) return;
        setBusy(true);
        try {
            await archiveWorkout(id);
            void navigate('/entrainement');
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Suppression impossible');
            setBusy(false);
        }
    };

    return (
        <div className="space-y-4 py-2">
            <header>
                <Link to="/entrainement" className="btn btn-ghost btn-sm mb-2 gap-2">
                    <ArrowLeft size={16} /> Entraînement
                </Link>
                <h1 className="text-2xl font-bold tracking-tight">
                    {creating ? 'Nouvelle séance' : 'Modifier la séance'}
                </h1>
            </header>

            {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}

            <section className="space-y-3 rounded-box bg-base-200 p-4">
                <label className="block">
                    <span className="mb-1 block text-sm font-medium">Nom</span>
                    <input type="text" maxLength={60} className="input input-bordered w-full"
                        placeholder="Échauffement du samedi"
                        value={name} onChange={(e) => setName(e.target.value)} />
                </label>
                <label className="block">
                    <span className="mb-1 block text-sm font-medium">Intention</span>
                    <input type="text" maxLength={200} className="input input-bordered w-full"
                        placeholder="Monter en puissance sans forcer"
                        value={description} onChange={(e) => setDescription(e.target.value)} />
                </label>
            </section>

            <section className="rounded-box bg-base-200 p-4">
                <h2 className="text-sm font-semibold">
                    Déroulé {steps.length > 0 && <span className="opacity-50">· {steps.length} exercice{steps.length > 1 ? 's' : ''}</span>}
                </h2>

                {steps.length === 0 ? (
                    <p className="mt-2 text-sm opacity-60">
                        Aucun exercice pour l'instant. Ajoutez-les dans l'ordre où vous
                        comptez les faire — c'est cet ordre que vous suivrez au practice.
                    </p>
                ) : (
                    <ol className="mt-3 space-y-2">
                        {steps.map((s, i) => (
                            <li key={s.key} className="flex items-center gap-2 rounded-field bg-base-100 p-2">
                                {/* Le rang, en clair : au practice on se repère à
                                    « j'en suis au 3 », pas au nom de l'exercice. */}
                                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-content">
                                    {i + 1}
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-sm font-semibold">{s.label}</span>
                                    <span className="block truncate text-xs opacity-60">{s.detail}</span>
                                </span>
                                <span className="flex shrink-0 items-center">
                                    <button type="button" aria-label={`Monter ${s.label}`}
                                        disabled={i === 0} onClick={() => move(i, -1)}
                                        className="btn btn-ghost btn-sm btn-square">
                                        <ChevronUp size={18} />
                                    </button>
                                    <button type="button" aria-label={`Descendre ${s.label}`}
                                        disabled={i === steps.length - 1} onClick={() => move(i, 1)}
                                        className="btn btn-ghost btn-sm btn-square">
                                        <ChevronDown size={18} />
                                    </button>
                                    <button type="button" aria-label={`Retirer ${s.label}`}
                                        onClick={() => setSteps((p) => p.filter((x) => x.key !== s.key))}
                                        className="btn btn-ghost btn-sm btn-square text-error">
                                        <X size={18} />
                                    </button>
                                </span>
                            </li>
                        ))}
                    </ol>
                )}

                <button type="button" onClick={() => setPicker(true)}
                    className="btn btn-outline btn-block mt-3 gap-2 tap-target">
                    <Plus size={18} /> Ajouter un exercice
                </button>
            </section>

            <button type="button" disabled={busy} onClick={() => void save()}
                className="btn btn-primary btn-block h-14 text-lg font-bold tap-target">
                {creating ? 'Créer la séance' : 'Enregistrer'}
            </button>

            {!creating && (
                <div className="pt-2">
                    <button type="button" className="btn btn-ghost btn-sm gap-2 text-error"
                        onClick={() => setConfirmDelete(true)}>
                        <Trash2 size={16} /> Supprimer cette séance
                    </button>
                </div>
            )}

            {picker && (
                <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60"
                    onClick={() => setPicker(false)}>
                    <div className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-t-box bg-base-200 p-4"
                        onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-bold">Ajouter un exercice</h2>
                            <button type="button" className="btn btn-ghost btn-sm btn-square"
                                aria-label="Fermer" onClick={() => setPicker(false)}>
                                <X size={20} />
                            </button>
                        </div>

                        <h3 className="mt-4 text-xs font-semibold uppercase tracking-wide opacity-50">
                            Modes intégrés
                        </h3>
                        <div className="mt-2 space-y-2">
                            {MODES.map((m) => (
                                <button key={m.mode} type="button" onClick={() => addMode(m.mode)}
                                    className="flex w-full items-center gap-3 rounded-field bg-base-100 p-3 text-left transition-colors hover:bg-base-300">
                                    <ModeIcon mode={m.mode} size={22} className="shrink-0 text-primary" />
                                    <span className="min-w-0">
                                        <span className="block font-semibold">{m.label}</span>
                                        <span className="block truncate text-xs opacity-60">{m.description}</span>
                                    </span>
                                </button>
                            ))}

                            {/* Le putting avec les modes intégrés : c'est un
                                exercice comme un autre pour qui compose son
                                practice, même si l'application le range
                                ailleurs. */}
                            <button type="button" onClick={addPutting}
                                className="flex w-full items-center gap-3 rounded-field bg-base-100 p-3 text-left transition-colors hover:bg-base-300">
                                <Target size={22} className="shrink-0 text-primary" />
                                <span className="min-w-0">
                                    <span className="block font-semibold">Putting</span>
                                    <span className="block truncate text-xs opacity-60">
                                        Trou après trou, sur le green. Seul ou à plusieurs.
                                    </span>
                                </span>
                            </button>
                        </div>

                        <h3 className="mt-5 text-xs font-semibold uppercase tracking-wide opacity-50">
                            Mes exercices
                        </h3>
                        {exercises.length === 0 ? (
                            <p className="mt-2 text-sm opacity-60">
                                Vous n'en avez pas encore créé.
                            </p>
                        ) : (
                            <div className="mt-2 space-y-2">
                                {exercises.map((e) => (
                                    <button key={e.id} type="button" onClick={() => addExercise(e)}
                                        className="flex w-full flex-col items-start rounded-field bg-base-100 p-3 text-left transition-colors hover:bg-base-300">
                                        <span className="font-semibold">{e.name}</span>
                                        <span className="text-xs opacity-60">
                                            {describe(e.ballsPerRound, e.rounds)}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Créer un exercice depuis le compositeur : on découvre
                            souvent qu'il manque une étape en écrivant la séance,
                            et faire faire l'aller-retour à la main ferait perdre
                            le brouillon en cours. On enregistre donc avant de
                            partir — mais seulement si la séance a un nom. */}
                        <Link to="/entrainement/exercice/nouveau"
                            className="btn btn-ghost btn-block mt-4 gap-2">
                            <Plus size={16} /> Créer un nouvel exercice
                        </Link>
                        {steps.length > 0 && (
                            <p className="mt-1 text-center text-xs opacity-50">
                                Enregistrez d'abord votre séance : ce déroulé n'est pas
                                encore sauvegardé.
                            </p>
                        )}
                    </div>
                </div>
            )}

            {confirmDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                    <div className="w-full max-w-sm rounded-box bg-base-200 p-5">
                        <h2 className="text-lg font-bold">Supprimer « {name} » ?</h2>
                        <p className="mt-2 text-xs opacity-70">
                            Les entraînements déjà faits avec cette séance restent dans votre
                            historique. Vos exercices ne sont pas supprimés.
                        </p>
                        <div className="mt-4 space-y-2">
                            <button type="button" disabled={busy} onClick={() => void remove()}
                                className="btn btn-error btn-block tap-target">Supprimer</button>
                            <button type="button" className="btn btn-ghost btn-block"
                                onClick={() => setConfirmDelete(false)}>Annuler</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/** « 3 balles × 5 séries », ou ce qu'il en reste quand c'est libre. */
export function describe(balls: number | null, rounds: number | null): string {
    if (balls === null && rounds === null) return 'Libre';
    if (rounds === null) return `${boundLabel(balls)} balle${balls !== null && balls > 1 ? 's' : ''} par série`;
    if (balls === null) return `${rounds} série${rounds > 1 ? 's' : ''}, balles libres`;
    return `${balls} balle${balls > 1 ? 's' : ''} × ${rounds} série${rounds > 1 ? 's' : ''}`;
}
